import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

export interface PorNarrativeMarkdownProps {
  source: string;
  /** Shown when `source` is empty or whitespace */
  emptyHint?: string;
  className?: string;
}

/**
 * Renders markdown written in Plan-of-Record narratives (sections, rationales).
 * Uses GFM lists/tables/task lists and preserves single newlines (`remark-breaks`).
 */
export function PorNarrativeMarkdown({ source, emptyHint, className = '' }: PorNarrativeMarkdownProps) {
  // Normalise stored text: literal '\n' escape sequences → real newlines,
  // then collapse runs of 3+ newlines to 2 (one blank paragraph break).
  const normalised = (source ?? '')
    .replace(/\\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n');
  const trimmed = normalised.trim();
  if (!trimmed) {
    return emptyHint ? (
      <p className="text-sm italic text-[var(--text-ter)] dark:text-[var(--text-ter)]">{emptyHint}</p>
    ) : null;
  }

  const proseCore = [
    'por-narrative-md prose prose-sm max-w-none',
    'prose-headings:font-semibold prose-headings:text-[color:var(--text-pri)]',
    'prose-p:text-[color:var(--text-sec)] prose-p:my-2 prose-p:leading-relaxed prose-p:break-words',
    'prose-li:text-[color:var(--text-sec)] prose-li:my-0.5 prose-li:break-words',
    'prose-strong:text-[color:var(--text-pri)] prose-strong:font-semibold',
    'prose-h1:text-base prose-h1:mb-2 prose-h1:mt-0 prose-h1:font-semibold',
    'prose-h2:text-[0.9375rem] prose-h2:mt-4 prose-h2:mb-2 prose-h2:font-semibold',
    'prose-h3:text-sm prose-h3:mt-3 prose-h3:mb-1 prose-h3:font-semibold',
    'prose-ul:my-2 prose-ol:my-2',
    'prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline dark:prose-a:text-primary-400',
    'prose-code:rounded prose-code:px-1 prose-code:py-0.5 prose-code:bg-black/[0.06] prose-code:text-[color:var(--text-pri)] prose-code:before:content-none prose-code:after:content-none dark:prose-code:bg-[var(--bg-raised)]/[0.08]',
    'prose-pre:bg-[color:var(--bg)] prose-pre:border prose-pre:border-[var(--rule)] prose-pre:text-[color:var(--text-pri)] prose-pre:text-xs',
    'prose-blockquote:border-l-primary-500 dark:prose-blockquote:border-l-primary-400 prose-blockquote:text-[color:var(--text-sec)]',
    'prose-hr:border-[var(--rule)]',
    'prose-th:text-[color:var(--text-pri)] prose-td:text-[color:var(--text-sec)]',
    'prose-table:border-collapse prose-table:text-sm prose-table:w-full prose-th:border prose-th:border-[var(--rule)] prose-td:border prose-td:border-[var(--rule)] prose-th:px-2 prose-th:py-1.5 prose-td:px-2 prose-td:py-1.5',
  ].join(' ');

  return (
    <div className={`${proseCore} ${className}`.trim()}>
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{trimmed}</ReactMarkdown>
    </div>
  );
}
